// Constraint drawing (style A) from the document's entries, in two layers around the grid lines
// (docs/CONSTRAINTS.md is the contract for the entries):
//   under: shaded cells, odd/even shapes, diagonals, lines and bulbs
//   over:  cages, arrows and circles, edge clues, quadruples, outside clues, labels
// Rules without a drawing (anti-knight, anti-king, non-consecutive, disjoint groups, negative
// "all dots given" entries) are told by the rules text only.
import type { ConstraintEntry, PuzzleDoc } from '@zinc/shared';
import { memo, useMemo } from 'react';
import type { Geometry } from '../../../lib/geometry';
import { Arrows } from './Arrows';
import { Killers } from './Cages';
import { CellMarks, CellShading, OddEven } from './Cells';
import { CornerLabel } from './CornerLabel';
import { Edges } from './Edges';
import { ofType, rcList, text, topLeft } from './geom';
import { Diagonals, Lines, PALE_LINE_TYPES } from './Lines';
import { OutsideClues } from './Outside';
import { Quadruples } from './Quadruples';
import { ENDS } from './style';

/**
 * `either` entries (one of several rules holds) draw each of their options, in grey unless the
 * option has its own colour: a grey line that is renban or entropic, a cell that is a minimum
 * or a maximum.
 */
function withOptions(doc: PuzzleDoc): PuzzleDoc {
  const options = doc.constraints
    .filter((e) => e.type === 'either' && Array.isArray(e.options))
    .flatMap((e) => e.options as ConstraintEntry[])
    .map((o) => ({
      ...o,
      style: { color: ENDS.color, ...(typeof o.style === 'object' && o.style ? o.style : {}) },
    }));
  return options.length ? { ...doc, constraints: [...doc.constraints, ...options] } : doc;
}

export { gridFrame, outsideMargins, type Frame, type Margins } from './geom';

export const ConstraintsUnder = memo(function ConstraintsUnder({
  doc: source,
  geometry,
}: {
  doc: PuzzleDoc;
  geometry: Geometry;
}) {
  const doc = useMemo(() => withOptions(source), [source]);
  return (
    <g pointerEvents="none" data-layer="constraints-under">
      <CellShading doc={doc} g={geometry} />
      <OddEven doc={doc} />
      <Diagonals doc={doc} />
      <Lines doc={doc} />
      <CellMarks doc={doc} />
    </g>
  );
});

/**
 * Labels of entries without a place of their own (cage sums, dot values and clue numbers show
 * their `label` in place): top-left of the first cell in reading order, like a cage sum.
 */
function Labels({ doc }: { doc: PuzzleDoc }) {
  const labelled = ofType(
    doc,
    ...PALE_LINE_TYPES,
    'double-arrow',
    'arrow',
    'extra-region',
    'odd',
    'even',
    'minimum',
    'maximum',
    'fortress',
    'clone',
    'magic-square',
    'quadruple',
  );
  return (
    <g>
      {labelled.map((e, i) => {
        const label =
          text(e, 'label') ||
          (e.type === 'product-line' && typeof e.product === 'number' ? `×${e.product}` : '');
        const at = topLeft(e.type === 'arrow' ? rcList(e, 'circle') : rcList(e));
        return label && at ? <CornerLabel key={i} cell={at} text={label} /> : null;
      })}
    </g>
  );
}

export const ConstraintsOver = memo(function ConstraintsOver({ doc: source }: { doc: PuzzleDoc }) {
  const doc = useMemo(() => withOptions(source), [source]);
  return (
    <g pointerEvents="none" data-layer="constraints-over">
      <Killers doc={doc} />
      <Arrows doc={doc} />
      <Edges doc={doc} />
      <Quadruples doc={doc} />
      <OutsideClues doc={doc} />
      <Labels doc={doc} />
    </g>
  );
});
